import { httpGet, httpPost } from '@/services/fetcher';
import {
  rejectWithLoggedServiceError,
  throwIfRequestCanceled,
} from '@/services/service-error-handler';
import URL from '@/services/urls';
import { urlParams } from '@/app/urlParams';
import { getConfiguredApiStageId } from '@/utils/stage/stage-utils';
import type {
  EndBoardResponse,
  EndGameResponse,
  GameStartResponse,
  GameStatusResponse,
  NextBoardResponse,
  SubmitMovesRequest,
  SubmitMovesResponse,
} from '@/types/api/game';
import axios, { type AxiosError } from 'axios';

interface GameApiEnvelope<T> {
  success: boolean;
  statusCode?: number;
  message?: string;
  data?: T;
}

interface GetStatusOptions {
  readonly silent?: boolean;
}

/** Next-board when the sequence has no further maze (still must call end-board after). */
function isNoMoreMazesNextBoardRaw(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null || !('success' in raw)) {
    return false;
  }
  const env = raw as {
    success: boolean;
    statusCode?: number;
    message?: string;
  };
  if (env.success !== false) {
    return false;
  }
  if (env.statusCode === 422) {
    return true;
  }
  const msg = env.message ?? '';
  return /no more mazes/i.test(msg);
}

/** End-game when session is already torn down (duplicate call) — treat as no-op. */
function isEndGameSessionAlreadyGoneRaw(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null || !('success' in raw)) {
    return false;
  }
  const env = raw as {
    success: boolean;
    statusCode?: number;
    message?: string;
    data?: { clearData?: boolean };
  };
  if (env.success !== false) {
    return false;
  }
  if (env.statusCode === 404 && env.data?.clearData === true) {
    return true;
  }
  if (env.statusCode === 404) {
    return true;
  }
  const msg = env.message ?? '';
  return /session not found/i.test(msg);
}

/** Backend wraps payloads in `{ success, data, message }`; support direct `data` for tests. */
function unwrapGamePayload<T>(payload: unknown, fallbackMessage: string): T {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'success' in payload &&
    'data' in payload
  ) {
    const envelope = payload as GameApiEnvelope<T>;
    if (!envelope.success || envelope.data === undefined) {
      throw new Error(envelope.message ?? fallbackMessage);
    }
    return envelope.data;
  }
  return payload as T;
}

const gameStart = async (): Promise<GameStartResponse> => {
  try {
    const stageId = getConfiguredApiStageId();
    const body: Record<string, string> = {};
    if (stageId) body.stageId = stageId;
    if (urlParams.gameSessionId) body.gameSessionId = urlParams.gameSessionId;
    if (urlParams.key) body.key = urlParams.key;
    const raw = await httpPost<unknown, typeof body>(URL.GAME_START, body);
    return unwrapGamePayload<GameStartResponse>(raw, 'Failed to start game');
  } catch (error) {
    rejectWithLoggedServiceError(error, 'Failed to start game:');
  }
};

const getStatus = async (options?: GetStatusOptions): Promise<GameStatusResponse> => {
  try {
    const stageId = getConfiguredApiStageId();
    const path = stageId
      ? `${URL.GAME_STATUS}?stageId=${encodeURIComponent(stageId)}`
      : URL.GAME_STATUS;
    const raw = await httpGet<unknown>(path);
    return unwrapGamePayload<GameStatusResponse>(raw, 'Failed to fetch game status');
  } catch (error) {
    const is404 = (error as { response?: { status?: number } })?.response?.status === 404;
    const shouldSuppress = Boolean(options?.silent && is404);
    rejectWithLoggedServiceError(error, 'Failed to fetch game status:', {
      fullSuppress: shouldSuppress,
    });
  }
};

const submitMoves = async (body: SubmitMovesRequest): Promise<SubmitMovesResponse> => {
  try {
    const stageId = getConfiguredApiStageId();
    const payload = stageId ? { stageId, ...body } : body;
    const raw = await httpPost<unknown, typeof payload>(URL.GAME_SUBMIT_MOVES, payload);
    return unwrapGamePayload<SubmitMovesResponse>(raw, 'Failed to submit moves');
  } catch (error) {
    rejectWithLoggedServiceError(error, 'Failed to submit moves:');
  }
};

const nextBoard = async (): Promise<NextBoardResponse | null> => {
  try {
    const stageId = getConfiguredApiStageId();
    const path = stageId
      ? `${URL.GAME_NEXT_BOARD}?stageId=${encodeURIComponent(stageId)}`
      : URL.GAME_NEXT_BOARD;
    const raw = await httpGet<unknown>(path);
    if (isNoMoreMazesNextBoardRaw(raw)) {
      return null;
    }
    return unwrapGamePayload<NextBoardResponse>(raw, 'Failed to fetch next board');
  } catch (error) {
    throwIfRequestCanceled(error);
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      return null;
    }
    const response = (error as AxiosError).response;
    const status = response?.status;
    const body = response?.data as { message?: string } | undefined;
    if (
      status === 422 ||
      (typeof body?.message === 'string' && /no more mazes/i.test(body.message))
    ) {
      return null;
    }
    rejectWithLoggedServiceError(error, 'Failed to fetch next board:');
  }
};

const endBoard = async (body?: { skip?: boolean }): Promise<EndBoardResponse> => {
  try {
    const stageId = getConfiguredApiStageId();
    const payload: Record<string, unknown> = {};
    if (stageId) payload.stageId = stageId;
    if (body?.skip !== undefined) payload.skip = body.skip;
    const raw = await httpPost<unknown, typeof payload>(URL.GAME_END_BOARD, payload);
    return unwrapGamePayload<EndBoardResponse>(raw, 'Failed to end board');
  } catch (error) {
    rejectWithLoggedServiceError(error, 'Failed to end board:');
  }
};

const endGame = async (): Promise<EndGameResponse | null> => {
  try {
    const stageId = getConfiguredApiStageId();
    const body = stageId ? { stageId } : {};
    const raw = await httpPost<unknown, typeof body>(URL.GAME_END_GAME, body);
    if (isEndGameSessionAlreadyGoneRaw(raw)) {
      return null;
    }
    return unwrapGamePayload<EndGameResponse>(raw, 'Failed to end game');
  } catch (error) {
    throwIfRequestCanceled(error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    const response = (error as AxiosError).response;
    if (response?.status === 404) {
      return null;
    }
    rejectWithLoggedServiceError(error, 'Failed to end game:');
  }
};

const gameService = {
  gameStart,
  getStatus,
  submitMoves,
  nextBoard,
  endBoard,
  endGame,
};

export default gameService;
